import { FolderOpen, LayoutGrid } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Field, Input, NativeSelect, StatCard } from "@ncp/ui";
import { CatalogPage } from "../features/generic/CatalogPage";
import { codeFromName, newServiceBody, type ServiceBody, type ServiceSummary } from "../features/services/api";

/** Services: every process the platform runs – compiled procedures and the ones admins assemble from a form, a workflow and roles. */
export function ServicesPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <CatalogPage<ServiceSummary, ServiceBody>
      resource="services"
      basePath="/services"
      icon={LayoutGrid}
      title={t("admin:serviceDesigner.title")}
      subtitle={t("admin:serviceDesigner.subtitle")}
      navLabel={t("admin:nav.services")}
      writePermission="platform.services.write"
      newLabel={t("admin:serviceDesigner.new")}
      emptyTitle={t("admin:serviceDesigner.emptyTitle")}
      emptyBody={t("admin:serviceDesigner.emptyBody")}
      docsAnchor="services"
      help={<><p>{t("admin:serviceDesigner.help.what")}</p><p>{t("admin:serviceDesigner.help.dynamic")}</p><p>{t("admin:serviceDesigner.help.release")}</p></>}
      newBody={(name, nameAr, extra) => newServiceBody(name, nameAr, extra)}
      createFields={(extra, set) => (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:serviceDesigner.fields.code")} hint={t("admin:serviceDesigner.fields.codeHint")}>
            <Input data-testid="create-code" value={extra.code ?? ""} maxLength={6} placeholder={codeFromName(extra.__name ?? "")} onChange={(e) => set("code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} />
          </Field>
          <Field label={t("admin:serviceDesigner.fields.audience")}>
            <NativeSelect data-testid="create-audience" value={extra.audience ?? "External"} onChange={(e) => set("audience", e.target.value)}>
              {(["External", "Internal", "Any"] as const).map((a) => <option key={a} value={a}>{t(`admin:serviceDesigner.audience.${a}`)}</option>)}
            </NativeSelect>
          </Field>
        </div>
      )}
      stats={(rows) => <StatCard label={t("admin:serviceDesigner.stats.open")} value={rows.reduce((n, r) => n + (r.openMatters ?? 0), 0)} icon={FolderOpen} tone="neutral" footer={t("admin:serviceDesigner.stats.openHint")} />}
      columns={[
        { header: t("admin:serviceDesigner.columns.kind"), cell: (r) => <Badge variant={r.moduleKind === "Dynamic" ? "gold" : "outline"} size="sm">{t(`admin:serviceDesigner.kind.${r.moduleKind}`)}</Badge> },
        { header: t("admin:serviceDesigner.columns.code"), cell: (r) => <span className="font-mono text-xs">{r.code}</span> },
        { header: t("admin:serviceDesigner.columns.category"), cell: (r) => (locale === "ar" && r.categoryAr ? r.categoryAr : r.category) },
        { header: t("admin:serviceDesigner.columns.workflow"), cell: (r) => <span className="font-mono text-xs">{r.workflowKey}</span> },
        { header: t("admin:serviceDesigner.columns.cases"), cell: (r) => <span className="tabular-nums">{r.openMatters} / {r.totalMatters}</span> },
      ]}
    />
  );
}
